/**
 * The terms of every sun shadow that the patched lookup reads beyond three's own (`splitShadowChunk.ts`),
 * in three's order of its sun shadows. Per sun: three rows of each slot's world-to-atlas matrix, the
 * direction towards the light with the soft shadow angle, each slot's range start, the fade, and
 * whether Godot draws the sun first. The fourth row of a slot matrix is always (0, 0, 0, 1), so
 * three rows hold it in three vectors where `mat4` takes four.
 */
import * as THREE from 'three';
import { SPLIT_SLOTS } from './splitShadow.js';
import { isSplitSun } from './splitSun.js';
import { LightUniformArrays } from '../shaderPatch/lightUniformArrays.js';
import { writeVec4 } from '../writeVec4.js';
export const SUN_SHADOW_ROWS = 'godotSunShadowRows';
export const SUN_SOFT_SHADOWS = 'godotSunSoftShadows';
export const SUN_RANGE_BEGINS = 'godotSunRangeBegins';
export const SUN_SHADOW_FADES = 'godotSunShadowFades';
export const SUN_DRAWN_FIRST = 'godotSunDrawnFirst';
/** The rows of one slot matrix that the lookup reads. */
export const SLOT_MATRIX_ROWS = 3;
/** `Matrix4.elements` holds four columns of four floats. */
const MATRIX_SIZE = 4;
const SLOT_FLOATS = SLOT_MATRIX_ROWS * MATRIX_SIZE;
const SUN_ROW_FLOATS = SPLIT_SLOTS * SLOT_FLOATS;
/** One vec4: the direction towards the light, and the angle. */
const SOFT_SHADOW_FLOATS = 4;
/** One vec2: the camera depths the fade runs between. */
const FADE_FLOATS = 2;
/** One float: 1 for the sun Godot draws first, else 0. */
const DRAWN_FIRST_FLOATS = 1;
/** Each array's floats per sun shadow. Written only by `writeSunShadowUniforms`. */
const arrays = new LightUniformArrays({
    [SUN_SHADOW_ROWS]: SUN_ROW_FLOATS,
    [SUN_SOFT_SHADOWS]: SOFT_SHADOW_FLOATS,
    [SUN_RANGE_BEGINS]: SPLIT_SLOTS,
    [SUN_SHADOW_FADES]: FADE_FLOATS,
    [SUN_DRAWN_FIRST]: DRAWN_FIRST_FLOATS,
});
/** Scratch for the writer, which runs on every shadow pass. */
const scratchDirection = new THREE.Vector3();
/** Gives the arrays to every built-in lit material. */
export function installSunShadowUniforms() {
    arrays.install();
}
/**
 * Writes the terms of each split sun among `lights`, in their order. Pass the lights of three's
 * shadow pass, after it has updated the slot matrices: three lists every sun shadow in that order
 * (r186 `WebGLLights.js:289-325`). It runs on every pass, so it allocates no list of suns.
 */
export function writeSunShadowUniforms(lights) {
    const sunCount = lights.reduce((count, light) => (isSplitSun(light) ? count + 1 : count), 0);
    if (sunCount === 0)
        return;
    arrays.write(sunCount, (set) => {
        let index = 0;
        for (const light of lights) {
            if (!isSplitSun(light))
                continue;
            writeSunRows(light, set[SUN_SHADOW_ROWS], index * SUN_ROW_FLOATS);
            writeSoftShadow(light, set[SUN_SOFT_SHADOWS], index);
            light.shadow.rangeBegins.toArray(set[SUN_RANGE_BEGINS], index * SPLIT_SLOTS);
            writeFade(light, set[SUN_SHADOW_FADES], index);
            set[SUN_DRAWN_FIRST][index] = light.shadow.isDrawnFirst ? 1 : 0;
            index++;
        }
    });
}
function writeSunRows(sun, rows, offset) {
    for (let slot = 0; slot < SPLIT_SLOTS; slot++) {
        writeSlotRows(sun.shadow.getMatrix(slot), rows, offset + slot * SLOT_FLOATS);
    }
}
/** `Matrix4.elements` is column-major, so row `r` is elements `r`, `r + 4`, `r + 8` and `r + 12`. */
function writeSlotRows(matrix, rows, offset) {
    const { elements } = matrix;
    for (let row = 0; row < SLOT_MATRIX_ROWS; row++) {
        for (let column = 0; column < MATRIX_SIZE; column++) {
            rows[offset + row * MATRIX_SIZE + column] = elements[column * MATRIX_SIZE + row];
        }
    }
}
/** The fitter places the sun at the light's offset from its target (`followDeclaredLight`). */
function writeSoftShadow(sun, softShadows, index) {
    const { x, y, z } = scratchDirection.setFromMatrixPosition(sun.matrixWorld).normalize();
    writeVec4(softShadows, index, x, y, z, sun.shadow.softShadowAngle);
}
/** A sun no fit has placed holds `NO_SHADOW_FADE`, whose ends do not ascend, so it fades nothing. */
function writeFade(sun, fades, index) {
    fades[index * FADE_FLOATS] = sun.shadow.fade.from;
    fades[index * FADE_FLOATS + 1] = sun.shadow.fade.to;
}
//# sourceMappingURL=sunShadowUniforms.js.map